import { useEffect } from 'react';

import { useGetEntityInfoQuery } from '@/components/organization-selection/services/orgSelectionApi';
import { useParentEntityId, useSelectedEntityId } from '@/hooks/useEntityScope';

/**
 * Prepay & Save gating (DV-2677), shared by the desktop header and the
 * mobile nav drawer. Emma-category accounts don't get the Prepay & Save
 * link. The category is always the *parent* account's — a sub-entity
 * inherits its parent's category rather than deriving one of its own.
 * `parentEntityId` is the top-level org (written at org selection,
 * unchanged by a sub-entity switch — the same handle `useEntityTreeNodes`
 * keys the tree fetch by), so we read the category off that entity;
 * `selectedEntity` is only a fallback for sessions predating that key.
 * While the category is unknown (loading, or entities with no networkName)
 * the link stays visible.
 */
export function usePrepaySaveGate(isLogin: boolean): boolean {
  const parentEntityId = useParentEntityId();
  const selectedEntityId = useSelectedEntityId();
  const categoryEntityId = isLogin ? parentEntityId ?? selectedEntityId ?? '' : '';
  const {
    data: entityInfo,
    isError: categoryLookupFailed,
    isLoading: categoryLookupInFlight
  } = useGetEntityInfoQuery(categoryEntityId, {
    skip: !categoryEntityId
  });
  const isEmmaAccount =
    entityInfo?.[0]?.additionalData?.networkName === 'Emma';
  // Fail-open governs every *settled* non-answer (failed lookup, missing
  // networkName, no entity to query), but applying it to the in-flight window
  // flashed the link at Emma accounts on each page load. Withholding it until
  // the lookup settles costs a non-Emma account a link that arrives a beat
  // late, which nobody can see; the flash was. `isLoading` is only ever true
  // for a request actually in flight — a skipped query leaves it false, so
  // sessions with no entity id still show the link immediately.
  const showPrepayLink = !isEmmaAccount && !categoryLookupInFlight;

  // The gate fails open on every non-answer, so "we couldn't find out" and
  // "found out, not Emma" render identically — a failed lookup silently
  // re-shows the link to an Emma account with nothing on screen to say so.
  // Log the one non-answer that is a genuine fault (loading resolves on its
  // own; a missing networkName is legitimate for some entities) so the case
  // is recoverable from a session replay.
  useEffect(() => {
    if (categoryLookupFailed) {
      console.error(
        'Prepay & Save gating: entity category lookup failed; showing the link.',
        { entityId: categoryEntityId }
      );
    }
  }, [categoryLookupFailed, categoryEntityId]);

  return showPrepayLink;
}
